import { Component, ElementRef, ViewChild } from '@angular/core';
import { iterate } from '@lib/iterate';
import { Csv } from '@lib/csv';
import { ImportMuckpileMapService } from './import-muckpile-map.service';
import { geojson } from '@lib/geojson';
import { fromLonLat } from 'ol/proj';
import { BlastSite, User } from '@models';
import { WebClient } from '@injectables';
import { DialogService } from '../dialog.service';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { MatDialogRef } from '@angular/material/dialog';
import { VehicleQueryService } from 'src/injectables/vehicle-query.service';
import { vantagePointMuckpileQuery } from '@models';
import { of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { asFiniteNumber } from '@lib/number';

export type ImportOptions = 'Manual' | 'CSV' | 'GeoJSON' | 'VantagePoint' | null;
type FileImportStep = 'upload' | 'configure' | 'preview';

const updateTypeFileExtentions: Record<Exclude<ImportOptions, null>, string[]> = {
	Manual: ['.'],
	CSV: ['csv'],
	GeoJSON: ['json'],
	VantagePoint: []
}

@Component({
	selector: 'dialog-import-muckpile',
	templateUrl: './dialog-import-muckpile.component.html',
	styleUrls: ['./dialog-import-muckpile.component.scss']
})
export class DialogImportMuckpileComponent {
	@ViewChild('importmap', { static: false }) mapElement: ElementRef;
	private _headersValid: boolean;
	public csvHeadersValid: boolean = true;
	public readonly trigger$ = this.fragQuery.trigger$;

	constructor(
		public readonly user: User,
		public readonly dialog: DialogService,
		private readonly webClient: WebClient,
		public readonly fragQuery: FragQueryService,
		private importMuckpileMapService: ImportMuckpileMapService,
		private dialogRef: MatDialogRef<DialogImportMuckpileComponent>,
		private readonly vehicleQuery: VehicleQueryService,
	) { }
	public fileErrorMessage: string = '';
    public validFile: boolean = false;
	public headerOptions: Record<string, string> = { '': '----' };
	public headersSwappable = [Object.keys(this.headerOptions)];
	public readonly blastRestClient = this.webClient.model.blastSite;
	public blastSite: BlastSite = new BlastSite;
	public _uploadType: ImportOptions = null;
	public step: FileImportStep = 'upload';
	public accepts: string;
	public file: File;
	public csv: Csv;
	public fileUploadText = 'Choose a file type above';
	public showReviewMap: boolean = false;
	public initialHeaders: string[];
	public readonly vpMuckpileManager$ = this.vehicleQuery.vpMuckpileManager$;
	public readonly showVpOption$ = this.fragQuery.siteId$.pipe(
		switchMap((siteId) => {
			const selectedSiteId = asFiniteNumber(siteId);
			if (!selectedSiteId) {
				return of(false);
			}

			return this.webClient.model.vantagePointConfig.search$({
				where: {
					siteId: selectedSiteId,
				},
				range: [0, 1],
			}).pipe(
				map((configs) => Array.isArray(configs) && configs.length > 0)
			);
		}),
		shareReplay(1),
	);
	public vpMuckpileTabIndex = 0;
	private readonly vpPendingActions = new Set<string>();
	public get uploadType() {
		return this._uploadType;
	}

	public set uploadType(v) {
		this._uploadType = v;
		this.file = null;
		this.step = 'upload';
		this.showReviewMap = false;
		this.fileErrorMessage = '';
		this.accepts = (updateTypeFileExtentions[v] ?? []).map(v => `.${v}`).join(',');
		this.fileUploadText = `Drag and drop a ${this._uploadType} file here or click to browse`;
		if (v === 'VantagePoint') {
			this.vehicleQuery.refreshVpMuckpileManager();
			this.vpMuckpileTabIndex = 0;
		}
	}

	public async onFileChange(file: File) {
		this.fileErrorMessage = '';
		this.validFile = false;
	
		if (!file) {
			this.fileErrorMessage = "No file selected.";
			return;
		}
	
		const fileType = file.name.split('.').pop().toLowerCase();
		const fileContents = await file.text();
	
		if (fileType === "json") {
			this.validateGeoJson(fileContents);
		} else {
			this.validFile = true;
		}
	}

	private validateGeoJson(fileContents: string) {
		try {
			const { features } = JSON.parse(fileContents);
			if (!Array.isArray(features) || !features[0]?.geometry) {
				throw new Error("Invalid GeoJSON format.");
			}
	
			const { geometry } = features[0];
			if (!Array.isArray(geometry.coordinates)) {
				throw new Error("Expected a Polygon geometry.");
			}
	
			const isValid = geometry.coordinates[0].every((coord: geojson.Coord) => 
				Array.isArray(coord) && coord.length === 2 && coord.every(c => typeof c === "number")
			);
	
			if (!isValid) {
				throw new Error("Invalid coordinate format.");
			}
	
			this.validFile = true;
		} catch (error) {
			const errMessage = (error as Error).message;
			console.error("GeoJSON Upload Error:", errMessage);
			this.fileErrorMessage = errMessage;
		}
	}

	private handleGeoJsonUpload(fileContents: string) {
		const geoJsonData = JSON.parse(fileContents);
		const coordinates = geoJsonData.features[0].geometry.coordinates;
		const transformedCoordinates = coordinates[0].map((coordinate: geojson.Coord) => fromLonLat(coordinate));
		this.importMuckpileMapService.initializeMap([0, 0], this.mapElement, transformedCoordinates);
		this.step = 'preview';
		this.showReviewMap = true
	}

	private async handleCsvUpload(csvContent: string) {
		const parsedCsv = Csv.parse(csvContent, true).normalizeColumnCount(true);
		// Ensure the CSV does not have more than 200 rows
		if (parsedCsv.rows.length > 200) {
			throw new Error(`More than 200 points cannot be processed. Please contact Support.`);
		}
		parsedCsv.headers = parsedCsv.headers.map(h => this.capitalizeFirstLetter(h));
		this.headerOptions = parsedCsv.headers.reduce<Record<string, string>>((acc, key: string) => {
			acc[key] = key;
			return acc;
		}, { '': '----' });
		this.headersSwappable = [Object.keys(this.headerOptions)];
		const lonHeader = parsedCsv.headers.find(h => /^Lon(gitude)?$/i.test(h)) || 'Lon';
		const latHeader = parsedCsv.headers.find(h => /^Lat(itude)?$/i.test(h)) || 'Lat';
		parsedCsv.headers[0] = lonHeader;
		parsedCsv.headers[1] = latHeader;
		this.initialHeaders = [...parsedCsv.headers];
		this.csv = parsedCsv;
	}

	private initialiseMap(file: Csv) {
		const shouldSwap = JSON.stringify(this.initialHeaders) !== JSON.stringify(file.headers);
		let numericCoordinates: geojson.Coord[] = file.rows.map(row => {
			let coords = row.map(value => asFiniteNumber(value)) as [number, number];
			return shouldSwap ? [coords[1], coords[0]] as geojson.Coord : coords;
		});
		const transformedCoordinates = numericCoordinates.map((coordinate) => fromLonLat(coordinate));
		this.importMuckpileMapService.initializeMap([0, 0], this.mapElement, transformedCoordinates);
		this.showReviewMap = true
	}

	public async handleFileNext(file: File) {
		const contents = await file.text();
		await this.handleCsvUpload(contents);
		this.step = 'preview';
	}

	public onHeadersChanged() {
		const fileHeaders = this.csv.headers?.slice() ?? [...iterate.numbers(0, this.csv.columnCount())].map(() => '');
		const headers = fileHeaders.map(() => '');

		let lonIndex = fileHeaders.findIndex(v => /^Lon|Longitude$/i.test(v));
		if (lonIndex >= 0) {
			fileHeaders[lonIndex] = this.capitalizeFirstLetter(fileHeaders[lonIndex]);
			headers[lonIndex] = fileHeaders[lonIndex];
		}
		let latIndex = fileHeaders.findIndex(v => /^Lat|Latitude$/i.test(v));
		if (latIndex >= 0) {
			fileHeaders[latIndex] = this.capitalizeFirstLetter(fileHeaders[latIndex]);
			headers[latIndex] = fileHeaders[latIndex];
		}

		this.csv.headers = headers;
		this.checkHeadersValidity(headers);
	}

	private capitalizeFirstLetter(text: string): string {
		return text.charAt(0).toUpperCase() + text.slice(1);
	}

	public goBack(type: string) {
		if (type === "upload" && this.showReviewMap && this.uploadType === 'CSV') {
			this.step = 'preview';
		}
		else if (this.step === 'preview') {
			this.step = 'upload';
		}
		this.showReviewMap = false;
		this.importMuckpileMapService.cleanup();
		this.blastSite.name = "";
	}

	public checkHeadersValidity(headers: string[]) {
		const headersValid = !!this.csv && headers.every(header => header.trim() !== '');
		if (this._headersValid !== headersValid) {
			this._headersValid = headersValid;
			this.csvHeadersValid = headersValid
		}
	}

	public async uploadFile(file: File) {
		this.blastSite.clientId = this.user.clientId;
		this.blastSite.siteId = this.fragQuery.siteId$.value ?? this.user.siteId;
		const fileType = file.name.split('.').pop().toLowerCase();
		let transformedCoordinates
		if (fileType === "json") {
			const contents = await file.text();
			const geoJsonData = JSON.parse(contents);
			const coordinates = geoJsonData.features[0].geometry.coordinates;
			transformedCoordinates = coordinates[0].map((coordinate: geojson.Coord) => fromLonLat(coordinate));
			this.blastSite.geometry = JSON.stringify({ type: "Polygon", coordinates: [transformedCoordinates] });
		} else {
			const shouldSwap = JSON.stringify(this.initialHeaders) !== JSON.stringify(this.csv.headers);
			let numericCoordinates: geojson.Coord[] = this.csv.rows.map(row => {
				let coords = row.map(value => asFiniteNumber(value)) as [number, number];
				return shouldSwap ? [coords[1], coords[0]] as geojson.Coord : coords;
			});
			transformedCoordinates = numericCoordinates.map(coordinate => fromLonLat(coordinate));
			this.blastSite.geometry = JSON.stringify({ type: "Polygon", coordinates: [transformedCoordinates] });
		}
		await this.dialog.await(this.blastRestClient.save(this.blastSite));
		this.trigger$.next();
		this.dialog.closeAll();
	}

	public async handleReviewOnMap(file: File) {
		if (!this.validFile) {
			this.fileErrorMessage = "Please upload a valid file before proceeding.";
			return;
		}
	
		const fileType = file.name.split('.').pop().toLowerCase();
		if (fileType === "json") {
			const contents = await file.text();
			this.handleGeoJsonUpload(contents);
		} else {
			this.initialiseMap(this.csv);
		}
	}

	public async showDrawingManager() {
		this.dialogRef.close({ uploadType: 'Manual' });
	}

	public isVpActionPending(actionKey: string) {
		return this.vpPendingActions.has(actionKey);
	}

	public trackByRegionGuid(_index: number, muckpile: { regionGuid: string }) {
		return muckpile.regionGuid;
	}

	public async addVpMuckpile(regionGuid: string) {
		await this.runVpAction(`add:${regionGuid}`, async () => {
			await this.vehicleQuery.handleVpMuckpileAction([regionGuid], 'add');
		});
	}

	public async syncVpMuckpile(regionGuid: string) {
		await this.runVpAction(`sync:${regionGuid}`, async () => {
			await this.vehicleQuery.handleVpMuckpileAction([regionGuid], 'sync');
		});
	}

	public async removeVpMuckpile(muckpile: vantagePointMuckpileQuery.list.ExistingMuckpile) {
		const confirmed = await this.dialog.confirm({
			message: `Remove "${muckpile.name}" from Netra? It will move back to the Non Existing Muckpiles list.`,
		});
		if (!confirmed) {
			return;
		}

		await this.runVpAction(`remove:${muckpile.regionGuid}`, async () => {
			await this.vehicleQuery.removeVpMuckpiles([muckpile.regionGuid]);
		});
	}

	private async runVpAction(actionKey: string, action: () => Promise<void>) {
		if (this.vpPendingActions.has(actionKey)) {
			return;
		}

		this.vpPendingActions.add(actionKey);
		try {
			await action();
		} finally {
			this.vpPendingActions.delete(actionKey);
		}
	}

	public cancel() {
		this.dialog.closeAll();
	}
}
